/**
 * Durable record of which sensitive pages the viewer has already consented to open.
 *
 * Passing a `SensitivePageGate` is a consent step, and asking for it again on every reload of a page the
 * viewer already opened is the behaviour this store exists to remove. It is deliberately client-side: this
 * is a low-stakes UX affordance, it has to work for anonymous viewers (who are defaulted to HIDE and so hit
 * the gate most often), and keeping it out of the database avoids storing a per-user record of which
 * sensitive things they chose to look at.
 *
 * Known trade-off, recorded in the owning design (§4 D4): `localStorage` survives across sessions on a
 * shared device. If that becomes a complaint, swap `readEntries`/`writeEntries` to `sessionStorage` — no
 * call site changes.
 */

export type SensitiveUnlockKind = "discussion" | "user" | "group" | "series";

const STORAGE_KEY = "sensitive_unlocks";

/**
 * Bounded so a long-lived browser cannot accumulate an unbounded list. Most-recent-first with eviction from
 * the tail, so the pages a viewer actually returns to stay unlocked.
 */
export const MAX_TRACKED_UNLOCKS = 200;

function buildEntryKey(kind: SensitiveUnlockKind, id: string | null | undefined): string | null {
    if (typeof id !== "string") {
        return null;
    }

    const normalizedId = id.trim();
    if (!normalizedId) {
        return null;
    }

    return `${kind}:${normalizedId}`;
}

function readEntries(): string[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return [];
        }

        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string") : [];
    } catch {
        // Unreadable, absent, or malformed storage means "nothing is unlocked" — never a thrown error. A gate
        // that throws is a page that does not render.
        return [];
    }
}

function writeEntries(entries: string[]): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
        // Safari private mode throws on setItem once the quota is zero. The unlock then lasts for this page
        // view only, which is a strictly better outcome than breaking the page.
    }
}

export function isSensitiveUnlocked(kind: SensitiveUnlockKind, id: string | null | undefined): boolean {
    const entryKey = buildEntryKey(kind, id);
    // A missing id must never report unlocked: it would collapse every unkeyed gate of that kind into one
    // shared unlock, so opening any one profile would open all of them.
    return entryKey != null && readEntries().includes(entryKey);
}

export function unlockSensitive(kind: SensitiveUnlockKind, id: string | null | undefined): void {
    const entryKey = buildEntryKey(kind, id);
    if (entryKey == null) {
        return;
    }

    const existing = readEntries().filter(entry => entry !== entryKey);
    writeEntries([entryKey, ...existing].slice(0, MAX_TRACKED_UNLOCKS));
}

export function clearSensitiveUnlocks(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Nothing to do — see writeEntries.
    }
}
